import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import MobileAuth from "@/pages/MobileAuth";

import Dashboard from "@/pages/Dashboard";

import ExamsList from "@/pages/student/ExamsList";
import ExamDetail from "@/pages/student/ExamDetail";
import TakeExam from "@/pages/student/TakeExam";
import Results from "@/pages/student/Results";
import ResultsList from "@/pages/student/ResultsList";
import Leaderboard from "@/pages/student/Leaderboard";

import QuestionBank from "@/pages/examiner/QuestionBank";
import QuestionForm from "@/pages/examiner/QuestionForm";
import ExamsManageList from "@/pages/examiner/ExamsManageList";
import ExamForm from "@/pages/examiner/ExamForm";

import UsersList from "@/pages/admin/UsersList";
import InstitutionsList from "@/pages/admin/InstitutionsList";
import InstitutionProfile from "@/pages/admin/InstitutionProfile";
import Profile from "@/pages/Profile";
import Forum from "@/pages/Forum";
import ForumThread from "@/pages/ForumThread";
import HelpDesk from "@/pages/HelpDesk";
import TicketDetail from "@/pages/TicketDetail";

import ProctorDashboard from "@/pages/proctor/ProctorDashboard";
import Wallet from "@/pages/Wallet";
import Coupons from "@/pages/admin/Coupons";
import VerifyCertificate from "@/pages/VerifyCertificate";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DeleteAccount from "@/pages/DeleteAccount";
import ForgotPassword from "@/pages/ForgotPassword";
import Pricing from "@/pages/Pricing";
import Browse from "@/pages/Browse";
import CompleteProfile from "@/pages/CompleteProfile";
import CookieBanner from "@/components/CookieBanner";
import MyCertificates from "@/pages/student/MyCertificates";
import AdminSettings from "@/pages/admin/AdminSettings";
import Analytics from "@/pages/Analytics";
import ReviewRequests from "@/pages/ReviewRequests";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle session_id in URL hash BEFORE other routes (no useEffect race)
  if (location.hash?.includes("session_id=") && location.pathname !== "/mobile-auth") {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/mobile-auth" element={<MobileAuth />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify/:cid" element={<VerifyCertificate />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/browse/:category" element={<Browse />} />

      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <CompleteProfile />
          </ProtectedRoute>
        }
      />

      {/* Full-screen exam taking (no sidebar) */}
      <Route
        path="/app/exam/:eid/take"
        element={
          <ProtectedRoute roles={["student"]}>
            <TakeExam />
          </ProtectedRoute>
        }
      />

      {/* App shell */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/app/exams" element={<ProtectedRoute><Layout><ExamsList /></Layout></ProtectedRoute>} />
      <Route path="/app/exam/:eid" element={<ProtectedRoute><Layout><ExamDetail /></Layout></ProtectedRoute>} />
      <Route path="/app/results" element={<ProtectedRoute><Layout><ResultsList /></Layout></ProtectedRoute>} />
      <Route path="/app/results/:aid" element={<ProtectedRoute><Layout><Results /></Layout></ProtectedRoute>} />
      <Route path="/app/leaderboard" element={<ProtectedRoute><Layout><Leaderboard /></Layout></ProtectedRoute>} />

      <Route path="/app/questions" element={<ProtectedRoute roles={["examiner", "institution_admin"]}><Layout><QuestionBank /></Layout></ProtectedRoute>} />
      <Route path="/app/questions/:qid" element={<ProtectedRoute roles={["examiner", "institution_admin"]}><Layout><QuestionForm /></Layout></ProtectedRoute>} />

      <Route path="/app/exams-manage" element={<ProtectedRoute roles={["examiner", "institution_admin"]}><Layout><ExamsManageList /></Layout></ProtectedRoute>} />
      <Route path="/app/exams-manage/:eid" element={<ProtectedRoute roles={["examiner", "institution_admin"]}><Layout><ExamForm /></Layout></ProtectedRoute>} />

      <Route path="/app/users" element={<ProtectedRoute roles={["institution_admin", "super_admin"]}><Layout><UsersList /></Layout></ProtectedRoute>} />
      <Route path="/app/institutions" element={<ProtectedRoute roles={["super_admin"]}><Layout><InstitutionsList /></Layout></ProtectedRoute>} />
      <Route path="/app/institution" element={<ProtectedRoute roles={["institution_admin"]}><Layout><InstitutionProfile /></Layout></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="/app/forum" element={<ProtectedRoute><Layout><Forum /></Layout></ProtectedRoute>} />
      <Route path="/app/forum/:tid" element={<ProtectedRoute><Layout><ForumThread /></Layout></ProtectedRoute>} />
      <Route path="/app/tickets" element={<ProtectedRoute><Layout><HelpDesk /></Layout></ProtectedRoute>} />
      <Route path="/app/tickets/:tid" element={<ProtectedRoute><Layout><TicketDetail /></Layout></ProtectedRoute>} />

      <Route path="/app/proctor" element={<ProtectedRoute roles={["proctor", "institution_admin"]}><Layout><ProctorDashboard /></Layout></ProtectedRoute>} />
      <Route path="/app/analytics" element={<ProtectedRoute roles={["institution_admin", "super_admin", "examiner"]}><Layout><Analytics /></Layout></ProtectedRoute>} />
      <Route path="/app/review-requests" element={<ProtectedRoute roles={["examiner", "institution_admin", "super_admin"]}><Layout><ReviewRequests /></Layout></ProtectedRoute>} />
      <Route path="/app/wallet" element={<ProtectedRoute><Layout><Wallet /></Layout></ProtectedRoute>} />
      <Route path="/app/coupons" element={<ProtectedRoute roles={["institution_admin", "super_admin"]}><Layout><Coupons /></Layout></ProtectedRoute>} />
      <Route path="/app/certificates" element={<ProtectedRoute><Layout><MyCertificates /></Layout></ProtectedRoute>} />
      <Route path="/app/settings" element={<ProtectedRoute roles={["institution_admin", "super_admin"]}><Layout><AdminSettings /></Layout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-zinc-900 focus:text-white focus:px-4 focus:py-2 focus:text-sm">Skip to main content</a>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
